import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  FaMobile,
  FaLaptopCode,
  FaCubes,
  FaRobot,
  FaShoppingCart,
  FaPalette,
  FaCheck,
  FaServer,
  FaPlug,
} from 'react-icons/fa';
import ProjectImage from '../components/ProjectImage';
import './ServicesPage.css';
import { Link } from 'react-router-dom';
import webDevImg from '../assets/services/web-dev.jpg';
import appDevImg from '../assets/services/app-dev.jpg';
import ecommerceDevImg from '../assets/services/ecommerce-dev.jpg';
import apiDevImg from '../assets/services/api-dev.jpg';
import aiDevImg from '../assets/services/ai-dev.jpg';
import saasDevImg from '../assets/services/saas-dev.jpg';
import uiDevImg from '../assets/services/ui-dev.jpg';

const ServicesPage = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.05,
  });

  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const services = [
    {
      icon: FaLaptopCode,
      title: 'Web Development',
      description: 'Build scalable, high-performance web applications tailored to your business needs.',
      features: [
        'Custom Web Application Development',
        'Information or Business Website',
        'Frontend Development',
        'Backend Development',
        'Database Management',
        'Testing & Quality Assurance',
        'Deployment & Hosting',
        'Responsive Website Design',
        'Content Management Systems',
        'SEO Optimization',
      ],
      technologies: [
        'React',
        'Next.js',
        'Node.js',
        'Express.js',
        'TypeScript',
        'Mongo DB',
        'MySQL',
        'Javascript',
        'Python',
        'Firebase',
        'Tailwind CSS',
        'Material UI',
      ],
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      images: [webDevImg],
    },
    {
      icon: FaMobile,
      title: 'Mobile App Development',
      description: 'Create powerful mobile experiences that engage users and drive business growth.',
      features: [
        'iOS & Android Native Development',
        'Cross-platform Solutions (React Native, Flutter)',
        'Progressive Web Apps (PWA)',
        'App Store Optimization',
        'Mobile UI/UX Design',
        'Backend Integration & APIs',
      ],
      technologies: ['React Native', 'Flutter', 'Node.js', 'Firebase'],
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      images: [appDevImg],
    },
    {
      icon: FaCubes,
      title: 'Blockchain Development',
      description: 'Build secure, transparent, and decentralized applications using blockchain technology.',
      features: [
        'Smart Contract Development',
        'DApp Development',
        'NFT Marketplace Development',
        'DeFi Development',
        'Cryptocurrency Integration',
        'Token Development (ERC-20, BEP-20)',
      ],
      technologies: ['Solidity', 'Ethereum', 'Web3.js', 'Ether.js', 'Hardhat', 'Truffle', 'IPFS'],
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      images: [webDevImg],
    },
    {
      icon: FaRobot,
      title: 'AI & Machine Learning',
      description: 'Harness the power of artificial intelligence to automate and innovate.',
      features: [
        'Custom AI Model Development',
        'Natural Language Processing',
        'AI Agents Development',
        'Predictive Analytics',
        'Chatbot Development',
        'ML Model Deployment',
        'LLM Integrations',
        'AI Automation',
      ],
      technologies: [
        'Python',
        'TensorFlow',
        'PyTorch',
        'scikit-learn',
        'LangChain',
        'Hugging Face Transformers',
        ' NLTK',
        'spaCy',
        'OpenAI',
      ],
      gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      images: [aiDevImg],
    },
    {
      icon: FaShoppingCart,
      title: 'E-Commerce Solutions',
      description: 'Build powerful online stores that convert visitors into customers.',
      features: [
        'Custom E-commerce Platforms',
        'Payment Gateway Integration',
        'Inventory Management',
        'Order Management Systems',
        'Multi-vendor Marketplaces',
        'Analytics & Reporting',
      ],
      technologies: ['MERN Stack', 'Shopify', 'Wordpress', 'Stripe', 'PayPal'],
      gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      images: [ecommerceDevImg],
    },

    {
      icon: FaServer,
      title: 'SaaS Product Development',
      description: 'Build scalable Software-as-a-Service platforms with robust features and architecture.',
      features: [
        'Multi-tenant Architecture',
        'Subscription & Billing Management',
        'Role-based Access Control',
        'Analytics & Reporting Dashboard',
        'API Development',
        'Cloud Infrastructure Setup',
      ],
      technologies: ['Node.js', 'React', 'Next.js', 'Typescript', 'MongoDB', 'MySQL', 'AWS'],
      gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
      images: [saasDevImg],
    },
    {
      icon: FaPlug,
      title: 'API & System Integration',
      description: 'Seamlessly connect your applications and services with third-party systems.',
      features: [
        'RESTful API Development',
        'GraphQL Implementation',
        'Third-party API Integration',
        'Web Socket Integrations',
      ],
      technologies: ['REST', 'GraphQL', 'Fast API', 'WebSockets', 'gRPC', 'OAuth'],
      gradient: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
      images: [apiDevImg],
    },
    {
      icon: FaPalette,
      title: 'UI/UX Design',
      description: 'Create beautiful, intuitive interfaces that users love to interact with.',
      features: [
        'User Research & Analysis',
        'Wireframing & Prototyping',
        'Visual Design',
        'Interaction Design',
        'Usability Testing',
        'Design Systems',
      ],
      technologies: ['Figma', 'Adobe XD', 'Sketch', 'InVision', 'Framer'],
      gradient: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
      images: [uiDevImg],
    },
  ];

  return (
    <div className="services-page">
      <section className="services-hero">
        <div className="services-hero-content">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <h1>Our Services</h1>
            <p>Comprehensive software solutions to transform your business and drive digital innovation</p>
          </motion.div>
        </div>
      </section>

      <section className="services-detailed" ref={ref}>
        <div className="services-container">
          {services.map((service, index) => (
            <motion.div
              key={index}
              className="service-detailed-card"
              initial={isMobile ? { opacity: 0, y: 20 } : { opacity: 0, y: 50 }}
              animate={inView || isMobile ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: isMobile ? index * 0.15 : index * 0.1 }}
            >
              <div className="service-layout">
                <div className="service-image-container">
                  <ProjectImage gradient={service.gradient} icon={service.icon} title={service.title} images={service.images} />
                </div>

                <div className="service-details">
                  <div className="service-header-inline">
                    <div className="service-icon-large" style={{ background: service.gradient }}>
                      <service.icon />
                    </div>
                    <div>
                      <h2>{service.title}</h2>
                      <p className="service-desc">{service.description}</p>
                    </div>
                  </div>

                  <div className="service-content">
                    <div className="service-features">
                      <h3>What We Offer</h3>
                      <ul>
                        {service.features.map((feature, i) => (
                          <li key={i}>
                            <FaCheck className="check-icon" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="service-technologies">
                      <h3>Technologies</h3>
                      <div className="tech-tags">
                        {service.technologies.map((tech, i) => (
                          <span key={i} className="tech-tag">
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <Link to="/contact">
                    <motion.button
                      className="service-cta-btn"
                      whileHover={isMobile ? {} : { scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      Get Started
                    </motion.button>
                  </Link>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="services-cta-section">
        <div className="cta-section-content">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <h2>Ready to Start Your Project?</h2>
            <p>Let's discuss how we can help you achieve your business goals</p>
                              <Link to="/contact">

            <motion.button
              className="cta-primary-btn"
              whileHover={isMobile ? {} : { scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Contact Us Today
            </motion.button>
                              </Link>

          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
